import { useRef, useEffect } from "react";
import { Bold, Italic, Underline, Heading2, Heading3, List, ListOrdered, Link2, Undo, Redo, AlignLeft, AlignCenter, AlignRight, AlignJustify } from "lucide-react";

const Btn = ({ onClick, title, children, testid }) => (
  <button
    type="button"
    title={title}
    data-testid={testid}
    onMouseDown={(e) => {
      e.preventDefault();
      onClick();
    }}
    className="h-9 w-9 flex items-center justify-center rounded hover:bg-slate-100 text-slate-700 transition-colors"
  >
    {children}
  </button>
);

export const RichTextEditor = ({ value, onChange }) => {
  const ref = useRef(null);

  useEffect(() => {
    if (ref.current && ref.current.innerHTML !== (value || "")) {
      ref.current.innerHTML = value || "";
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const exec = (command, arg = null) => {
    document.execCommand(command, false, arg);
    if (ref.current) onChange(ref.current.innerHTML);
  };

  const handleInput = () => {
    if (ref.current) onChange(ref.current.innerHTML);
  };

  const addLink = () => {
    const url = window.prompt("Enter URL");
    if (url) exec("createLink", url);
  };

  return (
    <div className="border border-slate-300 rounded-lg overflow-hidden bg-white" data-testid="rich-text-editor">
      <div className="flex flex-wrap items-center gap-0.5 border-b border-slate-200 bg-slate-50 px-2 py-1">
        <Btn title="Bold" onClick={() => exec("bold")} testid="editor-bold"><Bold className="h-4 w-4" /></Btn>
        <Btn title="Italic" onClick={() => exec("italic")} testid="editor-italic"><Italic className="h-4 w-4" /></Btn>
        <Btn title="Underline" onClick={() => exec("underline")} testid="editor-underline"><Underline className="h-4 w-4" /></Btn>
        <span className="w-px h-6 bg-slate-200 mx-1" />
        <Btn title="Heading" onClick={() => exec("formatBlock", "<h2>")} testid="editor-h2"><Heading2 className="h-4 w-4" /></Btn>
        <Btn title="Subheading" onClick={() => exec("formatBlock", "<h3>")} testid="editor-h3"><Heading3 className="h-4 w-4" /></Btn>
        <Btn title="Paragraph" onClick={() => exec("formatBlock", "<p>")} testid="editor-p"><span className="text-xs font-semibold">P</span></Btn>
        <span className="w-px h-6 bg-slate-200 mx-1" />
        <Btn title="Bullet list" onClick={() => exec("insertUnorderedList")} testid="editor-ul"><List className="h-4 w-4" /></Btn>
        <Btn title="Numbered list" onClick={() => exec("insertOrderedList")} testid="editor-ol"><ListOrdered className="h-4 w-4" /></Btn>
        <Btn title="Link" onClick={addLink} testid="editor-link"><Link2 className="h-4 w-4" /></Btn>
        <span className="w-px h-6 bg-slate-200 mx-1" />
        <Btn title="Align left" onClick={() => exec("justifyLeft")} testid="editor-align-left"><AlignLeft className="h-4 w-4" /></Btn>
        <Btn title="Align center" onClick={() => exec("justifyCenter")} testid="editor-align-center"><AlignCenter className="h-4 w-4" /></Btn>
        <Btn title="Align right" onClick={() => exec("justifyRight")} testid="editor-align-right"><AlignRight className="h-4 w-4" /></Btn>
        <Btn title="Justify" onClick={() => exec("justifyFull")} testid="editor-align-justify"><AlignJustify className="h-4 w-4" /></Btn>
        <span className="w-px h-6 bg-slate-200 mx-1" />
        <Btn title="Undo" onClick={() => exec("undo")} testid="editor-undo"><Undo className="h-4 w-4" /></Btn>
        <Btn title="Redo" onClick={() => exec("redo")} testid="editor-redo"><Redo className="h-4 w-4" /></Btn>
      </div>
      <div
        ref={ref}
        contentEditable
        onInput={handleInput}
        data-placeholder="Write the school listing content here…"
        data-testid="admin-rich-content-editor"
        className="bsd-editor bsd-prose min-h-[320px] max-h-[520px] overflow-y-auto px-5 py-4 focus:outline-none text-slate-700"
      />
    </div>
  );
};
